// ── combat.js ────────────────────────────────────────────────────────────────
// Advanced combat layer per combat.md: enemy archetypes, telegraphs (Lane /
// Pulse / Sweep / Safe Pocket) and QTE state (Escape Slider / Sequence
// Intercept / Precision Breach).
//
// Telegraph objects are pushed into st.telegraphs and consumed by the main
// loop to render warning zones and apply damage when they "fire."
//
// QTE objects are pushed into st.qtes and consumed by the React overlay;
// the overlay calls resolveQte(st, id, success) when the player completes one.
// ─────────────────────────────────────────────────────────────────────────────

// ── Archetype definitions ────────────────────────────────────────────────────
// Each archetype maps to a role described in combat.md §2.
//
// role:            "turret" | "interceptor" | "frigate" | "guardian"
// telegraph:       function(e, st, W, H) → pushes a telegraph onto st.telegraphs
// qte (optional):  function(e, st) → pushes a QTE onto st.qtes (cooldown-gated)
// ─────────────────────────────────────────────────────────────────────────────

export const ARCHETYPE_TURRET      = "turret";
export const ARCHETYPE_INTERCEPTOR = "interceptor";
export const ARCHETYPE_FRIGATE     = "frigate";
export const ARCHETYPE_GUARDIAN    = "guardian";

// Enemy count of each archetype scales with sector depth.
export function pickArchetypesForSector(sectorIdx, isGateOut) {
  if (isGateOut) return [ARCHETYPE_GUARDIAN];
  const out = [];
  // Turrets: always some
  out.push(ARCHETYPE_TURRET);
  if (sectorIdx >= 1) out.push(ARCHETYPE_TURRET);
  // Interceptors: appear from sector 2
  if (sectorIdx >= 2) out.push(ARCHETYPE_INTERCEPTOR);
  // Frigates: appear from sector 4
  if (sectorIdx >= 4) out.push(ARCHETYPE_FRIGATE);
  // Extra random elites with depth
  if (sectorIdx >= 6) out.push(Math.random() < 0.5 ? ARCHETYPE_INTERCEPTOR : ARCHETYPE_FRIGATE);
  return out;
}

export const ARCHETYPE_STATS = {
  [ARCHETYPE_TURRET]: {
    hp: 120, speed: 0.9, dmg: 10, fireRate: 180, color: "255,80,80", tier: 1,
    size: 14, mobile: true, telegraphCd: 180, qteCd: 0,
  },
  [ARCHETYPE_INTERCEPTOR]: {
    hp: 180, speed: 1.6, dmg: 6, fireRate: 240, color: "255,120,30", tier: 2,
    size: 12, mobile: true, telegraphCd: 360, qteCd: 600,
  },
  [ARCHETYPE_FRIGATE]: {
    hp: 260, speed: 0.5, dmg: 14, fireRate: 280, color: "255,60,200", tier: 2,
    size: 16, mobile: true, telegraphCd: 420, qteCd: 720,
  },
  [ARCHETYPE_GUARDIAN]: {
    hp: 1200, speed: 0.3, dmg: 18, fireRate: 220, color: "255,220,80", tier: 3,
    size: 28, mobile: true, telegraphCd: 300, qteCd: 480, invulnerable: true,
  },
};

// ── Telegraph helpers ────────────────────────────────────────────────────────
// A telegraph has: kind, warnFrames, activeFrames, geometry, dmg, author (enemy id).
// Life counts down every frame; when warnFrames reaches 0, telegraph enters
// "active" state (damaging) for activeFrames, then is removed.

export function pushLaneTelegraph(st, e, W, H) {
  // Horizontal rectangle from turret through the player area
  const ang = Math.atan2(st.shipY - e.y, st.shipX - e.x);
  st.telegraphs.push({
    kind: "lane", author: e.id, ang,
    ox: e.x, oy: e.y,
    width: 36, length: Math.max(W, H) * 1.2,
    warn: 60, active: 18, life: 78,
    dmg: e.dmg,
  });
}

export function pushPulseTelegraph(st, e) {
  // Circular mine at interceptor's current position
  st.telegraphs.push({
    kind: "pulse", author: e.id,
    ox: e.x, oy: e.y,
    radius: 70,
    warn: 75, active: 14, life: 89,
    dmg: e.dmg,
  });
}

export function pushSweepTelegraph(st, e) {
  // Arc that sweeps across the screen toward the player's current position
  const startAng = Math.atan2(st.shipY - e.y, st.shipX - e.x) - 0.6;
  st.telegraphs.push({
    kind: "sweep", author: e.id,
    ox: e.x, oy: e.y,
    ang: startAng, arc: 1.2,
    inner: 40, outer: 260,
    warn: 80, active: 30, life: 110,
    dmg: e.dmg,
  });
}

export function pushSafePocketTelegraph(st, e, W, H) {
  // Entire arena is hazard except for a moving pocket (Guardian phase)
  const ang = Math.random() * Math.PI * 2;
  const r   = Math.min(W, H) * 0.25;
  st.telegraphs.push({
    kind: "safe_pocket", author: e.id,
    ox: W / 2 + Math.cos(ang) * r, oy: H / 2 + Math.sin(ang) * r,
    vx: Math.cos(ang + Math.PI / 2) * 0.8,
    vy: Math.sin(ang + Math.PI / 2) * 0.8,
    radius: 70,
    warn: 90, active: 90, life: 180,
    dmg: e.dmg * 1.2,
  });
}

// ── Telegraph resolution ─────────────────────────────────────────────────────
// Called each frame from the main loop. Returns damage dealt to the player
// this frame (0 if player safe or iFrames).
export function updateTelegraphs(st, W, H) {
  let playerDmg = 0;
  for (let i = st.telegraphs.length - 1; i >= 0; i--) {
    const t = st.telegraphs[i];
    t.life--;
    if (t.warn > 0) t.warn--;

    // Move safe-pocket telegraphs
    if (t.kind === "safe_pocket") {
      t.ox += t.vx; t.oy += t.vy;
      // Bounce off arena bounds
      if (t.ox < 40 || t.ox > W - 40) t.vx *= -1;
      if (t.oy < 40 || t.oy > H - 40) t.vy *= -1;
    }

    // Active damage check — only once telegraph has charged up
    if (t.warn === 0 && t.active > 0) {
      t.active--;
      if (st.iFrames === 0 && playerInTelegraph(t, st.shipX, st.shipY)) {
        playerDmg += t.dmg;
        // Apply single tick per telegraph (consume active window)
        t.active = 0;
      }
    }
    if (t.life <= 0 || (t.warn === 0 && t.active <= 0)) {
      st.telegraphs.splice(i, 1);
    }
  }
  return playerDmg;
}

function playerInTelegraph(t, px, py) {
  if (t.kind === "lane") {
    // Rotate player into lane-local space and check rectangle hit
    const dx = px - t.ox, dy = py - t.oy;
    const lx = dx * Math.cos(-t.ang) - dy * Math.sin(-t.ang);
    const ly = dx * Math.sin(-t.ang) + dy * Math.cos(-t.ang);
    return lx > 0 && lx < t.length && Math.abs(ly) < t.width / 2;
  }
  if (t.kind === "pulse") {
    const dx = px - t.ox, dy = py - t.oy;
    return Math.sqrt(dx * dx + dy * dy) < t.radius;
  }
  if (t.kind === "sweep") {
    const dx = px - t.ox, dy = py - t.oy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < t.inner || dist > t.outer) return false;
    let a = Math.atan2(dy, dx) - t.ang;
    while (a >  Math.PI) a -= Math.PI * 2;
    while (a < -Math.PI) a += Math.PI * 2;
    return a >= 0 && a <= t.arc;
  }
  if (t.kind === "safe_pocket") {
    // Inverse: player IS damaged unless inside the pocket
    const dx = px - t.ox, dy = py - t.oy;
    return Math.sqrt(dx * dx + dy * dy) > t.radius;
  }
  return false;
}

// ── QTE helpers ──────────────────────────────────────────────────────────────
// QTE types:
//   "slider" — moving bar; tap when indicator is over green zone (Interceptor)
//   "sequence" — 4 numbered projectiles; tap in order (Frigate)
//   "breach"  — rotating ring; tap when arrow is in lit sector (Guardian)

let qteIdSeq = 0;
function nextQteId() { return ++qteIdSeq; }

export function pushSliderQte(st, e, sliderWidth) {
  // Only one QTE per enemy at a time
  if (st.qtes.some(q => q.author === e.id)) return;
  st.qtes.push({
    id: nextQteId(), kind: "slider", author: e.id,
    greenStart: 0.5 - sliderWidth / 2,
    greenEnd:   0.5 + sliderWidth / 2,
    indicator: 0,
    speed: 0.012,
    dir: 1,
    timeLeft: 6000, // ms
    label: "ESCAPE SLIDER — Tap in green",
    fail: { tether: 300 }, // on fail, player loses movement for 300 frames
  });
  // While the QTE is pending, the interceptor tethers the player (slow)
  e.tethering = true;
}

export function pushSequenceQte(st, e, mitigation) {
  if (st.qtes.some(q => q.author === e.id)) return;
  // 4 numbered targets to tap in ascending order
  const slots = [1, 2, 3, 4].sort(() => Math.random() - 0.5);
  st.qtes.push({
    id: nextQteId(), kind: "sequence", author: e.id,
    slots, next: 1,
    timeLeft: 5000,
    label: "SEQUENCE INTERCEPT — Tap 1→4",
    fail: { dmg: e.dmg * 2 * (1 - mitigation) },
  });
}

export function pushBreachQte(st, e, slow) {
  if (st.qtes.some(q => q.author === e.id)) return;
  st.qtes.push({
    id: nextQteId(), kind: "breach", author: e.id,
    ang: 0,
    speed: 0.06 * (1 - slow),
    lit: Math.random() * Math.PI * 2,
    litArc: 0.55,
    timeLeft: 8000,
    label: "PRECISION BREACH — Tap in lit arc",
    reward: { stripArmor: true }, // on success, Guardian becomes vulnerable for a window
  });
}

export function updateQtes(st, dtMs) {
  for (let i = st.qtes.length - 1; i >= 0; i--) {
    const q = st.qtes[i];
    q.timeLeft -= dtMs;
    if (q.kind === "slider") {
      q.indicator += q.speed * q.dir;
      if (q.indicator > 1) { q.indicator = 1; q.dir = -1; }
      if (q.indicator < 0) { q.indicator = 0; q.dir = 1; }
    }
    if (q.kind === "breach") {
      q.ang += q.speed;
    }
    if (q.timeLeft <= 0) {
      // Expire = failure
      resolveQte(st, q.id, false);
    }
  }
}

// Resolve a QTE by id. Applies rewards/penalties via the supplied callback map.
// handlers: { onHit, onTether, onStripArmor, onClear }
export function resolveQte(st, id, success, handlers = {}) {
  const q = st.qtes.find(x => x.id === id);
  if (!q) return;
  const author = st.enemies.find(e => e.id === q.author);

  if (success) {
    if (q.kind === "slider") {
      // Clear the tether on the interceptor
      if (author) author.tethering = false;
    }
    if (q.kind === "breach" && q.reward?.stripArmor && author) {
      author.armorStripped = 240; // frames of vulnerability
      if (handlers.onStripArmor) handlers.onStripArmor(author);
    }
    // Successful QTE damages the author
    if (author) {
      const dmg = q.kind === "breach" ? 250 : 60;
      author.hp -= dmg;
      author.hitFlash = 1;
    }
    if (handlers.onClear) handlers.onClear(q);
  } else {
    if (q.kind === "slider" && q.fail?.tether && author) {
      // Keep tether; just extend duration by denying escape
      st.iFrames = 0;
      if (handlers.onTether) handlers.onTether(q.fail.tether);
    }
    if (q.kind === "sequence" && q.fail?.dmg) {
      if (handlers.onHit) handlers.onHit(q.fail.dmg);
    }
  }
  // Remove the QTE
  st.qtes = st.qtes.filter(x => x.id !== id);
}

// ── Rendering helpers ────────────────────────────────────────────────────────

export function drawTelegraphs(ctx, telegraphs, W, H) {
  for (const t of telegraphs) {
    const active = t.warn === 0 && t.active > 0;
    const warnFrac = t.warn > 0 ? 1 - (t.warn / 60) : 1;
    const alpha = active ? 0.45 : 0.10 + warnFrac * 0.18;
    const strokeAlpha = active ? 0.9 : 0.35 + warnFrac * 0.35;
    const fill   = active ? `rgba(255,60,60,${alpha})`  : `rgba(255,180,40,${alpha})`;
    const stroke = active ? `rgba(255,120,80,${strokeAlpha})` : `rgba(255,200,60,${strokeAlpha})`;

    ctx.save();
    if (t.kind === "lane") {
      ctx.translate(t.ox, t.oy);
      ctx.rotate(t.ang);
      ctx.fillStyle   = fill;
      ctx.strokeStyle = stroke;
      ctx.lineWidth   = 1.5;
      ctx.fillRect(0, -t.width / 2, t.length, t.width);
      ctx.strokeRect(0, -t.width / 2, t.length, t.width);
    }
    if (t.kind === "pulse") {
      ctx.fillStyle   = fill;
      ctx.strokeStyle = stroke;
      ctx.lineWidth   = 2;
      ctx.beginPath(); ctx.arc(t.ox, t.oy, t.radius, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    }
    if (t.kind === "sweep") {
      ctx.fillStyle   = fill;
      ctx.strokeStyle = stroke;
      ctx.lineWidth   = 2;
      ctx.beginPath();
      ctx.arc(t.ox, t.oy, t.outer, t.ang, t.ang + t.arc);
      ctx.arc(t.ox, t.oy, t.inner, t.ang + t.arc, t.ang, true);
      ctx.closePath(); ctx.fill(); ctx.stroke();
    }
    if (t.kind === "safe_pocket") {
      // Fill whole arena hazard (red) with a cut-out green circle
      ctx.fillStyle = active ? "rgba(255,20,40,0.35)" : `rgba(255,160,40,${0.15 + warnFrac * 0.20})`;
      ctx.fillRect(0, 0, W, H);
      ctx.globalCompositeOperation = "destination-out";
      ctx.beginPath(); ctx.arc(t.ox, t.oy, t.radius, 0, Math.PI * 2); ctx.fill();
      ctx.globalCompositeOperation = "source-over";
      ctx.strokeStyle = active ? "#44ff99" : "#99ffcc";
      ctx.lineWidth = 2.5;
      ctx.beginPath(); ctx.arc(t.ox, t.oy, t.radius, 0, Math.PI * 2); ctx.stroke();
    }
    ctx.restore();
  }
}
